// Pure formatting and grouping helpers. Kept free of React and Firebase so the
// display logic is testable without either.

export const formatMoney = (n) =>
  n == null ? 'No price' : `$${Math.round(n).toLocaleString('en-US')}`

export const formatMiles = (n) =>
  n == null ? 'Mileage n/a' : `${Math.round(n).toLocaleString('en-US')} mi`

export const formatDistance = (n) => (n == null ? null : `${Math.round(n)} mi away`)

export function relativeTime(iso, now = Date.now()) {
  if (!iso) return 'never'
  const diff = now - new Date(iso).getTime()
  if (Number.isNaN(diff)) return 'never'
  if (diff < 0) return 'just now'
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'yesterday' : `${days}d ago`
}

/** Latest price move, if any. Returns null when there's nothing to compare. */
export function priceChange(listing) {
  const history = listing?.priceHistory
  if (!Array.isArray(history) || history.length < 2) return null
  const to = history[history.length - 1].price
  const from = history[history.length - 2].price
  if (from == null || to == null || from === to) return null
  return { from, to, dropped: to < from, delta: to - from }
}

/** Lowest price this listing has ever shown — the number worth negotiating from. */
export function lowestPrice(listing) {
  const prices = (listing?.priceHistory || []).map((h) => h.price).filter((p) => p != null)
  return prices.length ? Math.min(...prices) : (listing?.price ?? null)
}

const byRank = (a, b) =>
  (b.rank ?? 0) - (a.rank ?? 0) || (a.price ?? Infinity) - (b.price ?? Infinity)

export const SECTION = {
  DISMISSED: 'dismissed',
  WORKING: 'working',
  INTERESTED: 'interested',
  GONE: 'gone',
  MATCHES: 'matches',
  SOFT: 'soft',
  TRACKED: 'tracked',
}

/**
 * Which section a bike belongs in. First rule that applies wins.
 *
 * The load-bearing case is a Working bike that MotoHunt delists: it stays in
 * Working with a Gone badge rather than dropping into a collapsed section,
 * because that is precisely the moment the operator needs to see it.
 */
export function placementOf(listing, note) {
  const status = note?.status || ''
  if (isDismissed(status)) return SECTION.DISMISSED
  if (status === 'working') return SECTION.WORKING
  if (status === 'interested') return SECTION.INTERESTED
  if (listing?.status === 'delisted') return SECTION.GONE
  if (listing?.matchTier === 'full') return SECTION.MATCHES
  if (listing?.matchTier === 'soft') return SECTION.SOFT
  return SECTION.TRACKED
}

/**
 * Splits listings into the sections the UI shows, honouring operator statuses.
 * `notes` is optional: with none, this behaves exactly as it did before the
 * workspace existed.
 */
export function groupListings(listingsObj, notes = null) {
  const all = Object.values(listingsObj || {})
  const out = {
    working: [], interested: [], matches: [], soft: [],
    tracked: [], delisted: [], dismissed: [], total: all.length,
  }
  // SECTION.GONE is the display name; the bucket keeps its original key so
  // existing callers and tests reading `.delisted` keep working.
  const bucket = {
    [SECTION.WORKING]: out.working,
    [SECTION.INTERESTED]: out.interested,
    [SECTION.MATCHES]: out.matches,
    [SECTION.SOFT]: out.soft,
    [SECTION.TRACKED]: out.tracked,
    [SECTION.GONE]: out.delisted,
    [SECTION.DISMISSED]: out.dismissed,
  }

  for (const l of all) bucket[placementOf(l, noteFor(notes, l.id))].push(l)

  for (const key of ['working', 'interested', 'matches', 'soft', 'tracked', 'dismissed']) {
    out[key].sort(byRank)
  }
  out.delisted.sort((a, b) =>
    String(b.delistedAt || '').localeCompare(String(a.delistedAt || '')))

  return out
}

/**
 * Dealer panels for the dealer view. A dealer with a bike being Worked sorts
 * first — that view is organised around who to call next.
 */
export function groupByDealer(listings, notes) {
  const groups = new Map()

  for (const l of listings) {
    const key = dealerKey(l.dealerName)
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: l.dealerName || DEALER_UNKNOWN_LABEL,
        listings: [],
        hasWorking: false,
      })
    }
    const g = groups.get(key)
    g.listings.push(l)
    if (noteFor(notes, l.id).status === 'working') g.hasWorking = true
  }

  for (const g of groups.values()) g.listings.sort(byRank)

  return [...groups.values()].sort(
    (a, b) =>
      Number(b.hasWorking) - Number(a.hasWorking) ||
      (b.listings[0]?.rank ?? 0) - (a.listings[0]?.rank ?? 0) ||
      a.name.localeCompare(b.name)
  )
}

/** Human-readable label for a soft-match tag. */
export const TAG_LABELS = {
  overMileage: 'Over mileage target',
  mileageUnknown: 'Mileage not listed',
  priceUnknown: 'Price not listed',
  distanceUnknown: 'Distance unknown',
}

// --- workspace: statuses, dealers, alert read-state -------------------------
// The operator's own data lives under /notes and /dealers, keyed by listing id
// and dealer slug. Everything here is pure so the placement and read-state
// rules are testable without React or Firebase.

export const STATUSES = ['interested', 'working', 'sold', 'notFound', 'passed']

export const STATUS_LABELS = {
  interested: 'Interested',
  working: 'Working',
  sold: 'Sold',
  notFound: 'Not found',
  passed: 'Passed',
}

/** Statuses that close a bike out. Not "gone" — that is MotoHunt's word, not yours. */
export const DISMISSED = new Set(['sold', 'notFound', 'passed'])

export const DEALER_UNKNOWN_KEY = 'unknown'
export const DEALER_UNKNOWN_LABEL = 'Private / unknown seller'

/**
 * Dealer name → RTDB key. Names are free text out of a dealer feed, and RTDB
 * rejects . $ # [ ] / in keys outright, so everything outside [a-z0-9] collapses
 * to a hyphen rather than being escaped.
 */
export function dealerKey(name) {
  const slug = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || DEALER_UNKNOWN_KEY
}

export const noteFor = (notes, id) => (notes && notes[id]) || {}

export const statusOf = (notes, listing) => noteFor(notes, listing?.id).status || ''

export const isDismissed = (status) => DISMISSED.has(status)

/**
 * A follow-up records what is owed, not what happened (that is
 * `lastContactAt`'s job). Due the moment its date arrives, and every day
 * after until the operator clears or moves it — a date that only reminds on
 * its exact day would silently stop reminding once missed.
 */
export function isFollowUpDue(note, now = Date.now()) {
  const at = note?.followUpAt
  if (!at) return false
  const due = new Date(at).getTime()
  if (Number.isNaN(due)) return false
  return due <= now
}

/** Alerted, and not acknowledged since. No alert means nothing to read. */
export function isUnread(listing, note) {
  const at = listing?.lastAlert?.at
  if (!at) return false
  const seen = note?.seenAlertAt
  if (!seen) return true
  return new Date(seen).getTime() < new Date(at).getTime()
}

/** Why the bike is in the inbox, in the operator's terms. */
export function alertLabel(listing) {
  const a = listing?.lastAlert
  if (!a) return null
  if (a.type === 'new') {
    if (a.variant !== 'nowInRange') return 'New match'
    // Same construction as the priceDrop branch below, and the same reason:
    // a.price is the price recorded when this alert fired, not the listing's
    // live price, which may have moved again since — the label describes
    // what the alert said, not what is true right now.
    const delta = (a.previousPrice ?? 0) - (a.price ?? 0)
    return a.previousPrice != null && a.price != null && delta > 0
      ? `Now in range — dropped ${formatMoney(delta)}`
      : 'Now in range'
  }
  if (a.type === 'delisted') return 'No longer listed'
  if (a.type === 'priceDrop') {
    const delta = (a.previousPrice ?? 0) - (a.price ?? 0)
    // Only call it a drop when both numbers are present and actually fell.
    return a.previousPrice != null && a.price != null && delta > 0
      ? `Price dropped ${formatMoney(delta)}`
      : 'Price changed'
  }
  return 'Update'
}

// --- view filter ------------------------------------------------------------
// A browsing tool over listings the crawler has already judged. It narrows what
// is on screen and never affects what matches or what alerts — that is the
// crawl criteria editor's job, and the two are labelled apart in the UI.

// Canonical list of link-health states the crawler can write to
// `listing.linkHealth.state`. `unchecked` is deliberately not in this list —
// it is not a state the crawler ever writes, it is what the filter and the
// UI call a listing that has none yet (`listing.linkHealth?.state || 'unchecked'`).
export const LINK_HEALTH_STATES = ['ok', 'missing', 'dead', 'blocked', 'robots-skipped', 'error']

// What each link-health state means in the operator's terms, and the icon
// used for it. Advisory only — nothing here ever changes a status; a dead
// link is a reason to phone the dealer, not a verdict that the bike is sold.
//
// Hoisted out of ListingCard, ListingTable and ListingDetail, which each
// carried their own copy of this map (two of the three byte-identical
// despite a comment claiming one was "abbreviated for a dense row") with
// nothing checking any of them against LINK_HEALTH_STATES. Both card/table
// call sites render `{health && …}`, so a state missing from whichever copy
// they happened to use would render as nothing — indistinguishable from
// "never checked" — with the test suite green. util.test.js asserts this map
// has an entry for every LINK_HEALTH_STATES value so that can't happen again.
export const LINK_HEALTH_INFO = {
  ok: { icon: '✓', text: 'Dealer link was live at the last check' },
  missing: { icon: '⚠', text: 'Link no longer on the dealer site — it redirected away or says sold' },
  dead: { icon: '✗', text: 'Dealer link returned an error' },
  blocked: { icon: '🔒', text: 'Dealer site blocked the check' },
  'robots-skipped': { icon: '–', text: "Dealer's robots.txt disallows checking this page" },
  error: { icon: '?', text: 'Check could not complete — the dealer site did not answer' },
  // Not a crawler-written state — see LINK_HEALTH_STATES above — but a real
  // display entry so a never-checked link shows something rather than
  // rendering as nothing at all, which is indistinguishable from a link the
  // card simply chose not to show a chip for.
  unchecked: { icon: '—', text: 'Dealer link has not been checked yet' },
}

export const EMPTY_FILTER = {
  priceMax: null,
  mileageMax: null,
  yearMin: null,
  yearMax: null,
  distanceMax: null,
  linkHealth: '',
  status: '',
  followUpDue: false,
  text: '',
}

// A null field on the listing passes every bound. 60% of listings have no
// mileage; hiding unknowns would bury them exactly when narrowing down.
const atMost = (value, bound) => bound == null || value == null || value <= bound
const atLeast = (value, bound) => bound == null || value == null || value >= bound

export function applyViewFilter(listings, notes, filter) {
  const f = { ...EMPTY_FILTER, ...(filter || {}) }
  const needle = (f.text || '').trim().toLowerCase()

  return listings.filter((l) => {
    if (!atMost(l.price, f.priceMax)) return false
    if (!atMost(l.mileage, f.mileageMax)) return false
    if (!atMost(l.distanceMiles, f.distanceMax)) return false
    if (!atLeast(l.year, f.yearMin)) return false
    if (!atMost(l.year, f.yearMax)) return false

    if (f.status && statusOf(notes, l) !== f.status) return false

    if (f.linkHealth) {
      const state = l.linkHealth?.state || 'unchecked'
      if (state !== f.linkHealth) return false
    }

    if (f.followUpDue && !isFollowUpDue(noteFor(notes, l.id))) return false

    if (needle) {
      const hay = [l.title, l.dealerName, l.location, l.stockNumber, l.vin]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!hay.includes(needle)) return false
    }

    return true
  })
}

const DAY_MS = 86_400_000
const daysSince = (then, now) => Math.max(0, Math.floor((now - then) / DAY_MS))

// MotoHunt publishes days-on-market directly on the detail page — the `Age`
// row of its spec table, captured as "16d" / "208d". Phase 2b stores it.
//
// This used to compute a FLOOR from listing.motohuntPriceDrop.on and render it
// as "Listed ≥40d", on the documented belief that "MotoHunt publishes no
// listing date, so a true age is not obtainable". Both halves were wrong: the
// date is published, and nothing in the crawler has ever written
// motohuntPriceDrop, so that branch was dead code the whole time.
//
// A bike sitting 208 days is the strongest negotiating signal this app can
// show, which is why this is worth getting exactly right rather than
// approximately.
const PUBLISHED_AGE = /^(\d+)d$/

export function ageLabel(listing, now = Date.now()) {
  // Source-controlled free text: render it only when it is plainly "<n>d".
  // Anything else falls through rather than reaching the UI verbatim.
  const published = String(listing?.specs?.Age ?? '').trim()
  if (PUBLISHED_AGE.test(published)) return `Listed ${published}`

  // Pre-2b listings, and any bike whose detail page could not be read.
  const first = listing?.firstSeenAt ? new Date(listing.firstSeenAt).getTime() : null
  if (first != null && !Number.isNaN(first)) return `Seen ${daysSince(first, now)}d`
  return null
}

/** Same shape MotoHunt's own "see map" link uses: dealer name plus location. */
export function mapsUrl(listing) {
  const q = [listing?.dealerName, listing?.location].filter(Boolean).join(' ')
  return q ? `https://maps.google.com/?q=${encodeURIComponent(q)}` : null
}
